/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { types } from 'mobx-state-tree';
import { toJS } from 'mobx';
import differenceBy from 'lodash/differenceBy';
import BulkProject from './BulkProject';
import {
  getEntityUrnsOrProductIdsBySearchParam,
  getProjects
} from '../services/getProjects';
import { isSameRequest } from '../utils';
import { DISTRIBUTABLE_URN_REGEX } from '../utils/regexPatterns';

let lastAPIParams = {};
let entityUrnsOrProductIds = {};

export default types
  .model('BulkProjectsList', {
    bulkProjects: types.array(BulkProject),
    bulkPageIndex: 1,
    currentBulkPageIndex: 0,
    hasMoreBulkResults: true,
    bulkProjectPublishStatus: types.frozen({}),
    totalBulkProjects: 0,
    loadBulkProjectsCount: 0,
    localPageIndex: 1,
    recordsPerPage: 100,
    updateSelectedRows: false,
    selectedRows: types.array(types.string),
    selectedProjects: types.array(BulkProject)
  })
  .views(self => ({
    // Computed property for paginated projects
    get bulkProjectPaginateRecords() {
      const start = (self.localPageIndex - 1) * self.recordsPerPage;
      const end = start + self.recordsPerPage;
      return self.bulkProjects.slice(start, end);
    }
  }))
  .actions(self => ({
    resetBulkProjects() {
      self.bulkProjects = [];
      self.totalBulkProjects = 0;
    },

    resetBulkPageIndex() {
      self.bulkPageIndex = 1;
      self.currentBulkPageIndex = 0;
    },

    resetBulkLastAPIParams() {
      lastAPIParams = {};
    },

    setHasMoreBulkResults(hasMoreBulkResults) {
      self.hasMoreBulkResults = hasMoreBulkResults;
    },

    resetPageIndexAndRecordPerPage() {
      self.localPageIndex = 1;
      self.recordsPerPage = 100;
    },

    resetAllBulkProjectsData(isLoadingRequired = true) {
      self.resetBulkProjects();
      self.resetBulkPageIndex();
      self.resetPageIndexAndRecordPerPage();
      self.setUpdateSelectedRows(true);
      if (!isLoadingRequired) {
        self.setHasMoreBulkResults(false);
      } else {
        self.setHasMoreBulkResults(true);
      }
    },
    setUpdateSelectedRows(value) {
      self.updateSelectedRows = value;
    },
    setTotalBulkProjectCount(count) {
      self.totalBulkProjects = count;
    },
    incrementLoadBulkProjectsCount() {
      self.loadBulkProjectsCount += 1;
    },
    updateBulkProjects(projects, hasPublishStatus = true) {
      projects.forEach(project => {
        self.addProject(project, hasPublishStatus);
      });
    },

    /**
     * Projects are fetched from Project API based on Query Params and Page index.
     * delete previous projects if resetProjects is true.
     * @param {*} params
     * @param {bool} resetProjects
     * @param {bool} useLastPageIndex - If true, will use last page index.
     */

    async loadBulkProjects(
      queryParams,
      resetProjects,
      useLastPageIndex,
      shouldFetchPublishStatus
    ) {
      const appliedPageIndex = useLastPageIndex
        ? self.currentBulkPageIndex
        : self.bulkPageIndex;

      const apiParams = {
        page: resetProjects ? 1 : appliedPageIndex,
        ...queryParams,
        viewMode: 'bulk'
      };

      // sanity block duplicate calls except when toggling off favorite icon with favorite filter selected.
      if (isSameRequest(lastAPIParams, apiParams) && !useLastPageIndex) {
        return;
      }

      lastAPIParams = apiParams;

      const shouldFetchDataFromC6OrCSG =
        apiParams.page === 1 &&
        (apiParams.projectPublishPlatform !== 'default' ||
          apiParams.projectPublishStatus !== 'default');

      const isSearchtermDUrn =
        apiParams.searchTerm &&
        apiParams.searchTerm.match(DISTRIBUTABLE_URN_REGEX);

      const isStatusOrPlatformNotSelected =
        apiParams.projectPublishPlatform === 'default' &&
        apiParams.projectPublishStatus === 'default';

      if (
        shouldFetchDataFromC6OrCSG &&
        !apiParams.useOldSearchAPI &&
        !isSearchtermDUrn
      ) {
        entityUrnsOrProductIds = await getEntityUrnsOrProductIdsBySearchParam(
          apiParams,
          useLastPageIndex,
          false
        );
      }

      // if status or platform not selected then no need to inject any data in request.
      if (isStatusOrPlatformNotSelected || isSearchtermDUrn) {
        entityUrnsOrProductIds = null;
      }

      // passing useLastPageIndex to skip canceling the load projects call
      const {
        projects = [],
        hasMore = false,
        totalResults = 0
      } = await getProjects(
        apiParams,
        useLastPageIndex,
        false,
        entityUrnsOrProductIds
      );

      // if there is a new search term, we have to reset the existing state of projects Array
      if (resetProjects) {
        self.resetAllBulkProjectsData();
      }

      self.setTotalBulkProjectCount(totalResults);
      self.setHasMoreBulkResults(hasMore);

      let projectList = projects;
      if (useLastPageIndex) {
        // Only passing new projects which is coming in API response and not already available in project model.
        projectList = differenceBy(projects, toJS(self.bulkProjects), 'id');
      }

      const hasPublishStatus = !shouldFetchPublishStatus;
      self.updateBulkProjects(projectList, hasPublishStatus);
      self.incrementLoadBulkProjectsCount();

      // if the API returns hasMore result except when toggling off favorite icon with favorite filter selected
      if (hasMore && !useLastPageIndex) {
        self.incrementBulkPageIndex();
      }
    },
    /**
     * Update Projects in the state
     * @param {*} projects
     * @param {bool} isPublishStatusLoaded
     */

    /**
     * If there are more results in the API, increment the page index (used for react-infinite-scroller) and current page index.
     */
    incrementBulkPageIndex() {
      self.bulkPageIndex += 1;
      self.currentBulkPageIndex += 1;
    },

    addProject(project) {
      const {
        id,
        name,
        authorName,
        entityUrn,
        status = {},
        productId,
        lineOfBusiness,
        dateCreated,
        publishInfo,
        previousDistributableUrn,
        isEpubProject,
        isTccProject,
        isEbookProject,
        isCypressPlusEnabled,
        isH5PContentEnabled,
        EnableTeacherAssistant,
        isSbrEnabled,
        isPearsonPlusEnabled,
        category,
        sharingContextRole
      } = project;

      const { projectStatus = '', publishStatus = null } = status;

      self.bulkProjects.push({
        id,
        name,
        authorName,
        entityUrn,
        status: projectStatus.toLowerCase(),
        publishStatus,
        publishDestination:
          publishInfo && publishInfo.publishDestination
            ? publishInfo.publishDestination
            : null,
        publishInfo,
        productId,
        dateCreated,
        lineOfBusiness,
        previousDistributableUrn,
        isEpubProject: Boolean(isEpubProject),
        isTccProject: Boolean(isTccProject),
        isEbookProject: Boolean(isEbookProject),
        isCypressPlusEnabled: Boolean(isCypressPlusEnabled),
        isH5PContentEnabled: Boolean(isH5PContentEnabled),
        EnableTeacherAssistant: Boolean(EnableTeacherAssistant),
        isSbrEnabled: Boolean(isSbrEnabled),
        isPearsonPlusEnabled: Boolean(isPearsonPlusEnabled),
        category: category || null,
        sharingContextRole: sharingContextRole || 'No Subscription'
      });
    },

    updatePublishStatus(statusObj) {
      self.bulkProjectPublishStatus = {
        ...self.bulkProjectPublishStatus,
        ...statusObj
      };
    },
    changeLocalPageIndex(newPageIndex, recordsPerPage) {
      self.localPageIndex = newPageIndex;
      self.recordsPerPage = recordsPerPage;
    },
    addSelectedProject(project) {
      if (!self.selectedProjects.find(p => p.id === project.id)) {
        self.selectedProjects.push(project);
      }
    },
    removeSelectedProject(id) {
      const idx = self.selectedProjects.findIndex(p => p.id === id);
      if (idx !== -1) self.selectedProjects.splice(idx, 1);
    },
    clearSelectedProjects() {
      self.selectedProjects.clear();
    }
  }));
